/**
 * Formulário de um documento pessoal.
 *
 * Papel: preenche, valida e grava um documento — pessoa, tipo, número, as duas
 * datas, o status e a observação —, servindo tanto à criação quanto à edição.
 *
 * Como usar: quem monta este componente passa `documento` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por documento. Sem a
 * `key`, o React reaproveita a mesma instância ao trocar de registro e os
 * campos continuam com o que a pessoa via antes, porque `defaultValues` só é
 * lido na montagem.
 *
 * As datas são digitadas em `DD/MM/AAAA` e entregues ao formulário já em ISO
 * pelo `CampoMascarado`: é `novoDocumentoSchema` que recebe o valor, e ele só
 * aceita ISO. Enquanto a digitação está pela metade o texto segue como está, e
 * o schema é quem recusa — o que faz a mensagem aparecer ao tentar salvar, e
 * não a cada tecla.
 *
 * O campo Documento é texto livre — `Passaporte BRA`, `CIN` —, e nada impede
 * cadastrar um segundo documento com o mesmo nome para a mesma pessoa. O 409
 * chega sem caminho de campo e por isso aparece acima dos botões: significa que
 * a pessoa escolhida foi excluída enquanto o formulário estava aberto.
 */
import { type Documento, type NovoDocumento, novoDocumentoSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { usePessoasFamilia } from '@/modules/cadastros-base';
import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { formatarPessoa } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { ROTULOS_STATUS, STATUS_NA_ORDEM_DO_FORMULARIO } from '../rotulos';
import { useSalvarDocumento } from '../hooks/usar-documentos';
import { DOCUMENTO_EM_BRANCO, type DocumentoNoFormulario } from '../types';

interface FormularioDocumentoProps {
  /** O documento a editar, ou `null` para criar um novo. */
  documento: Documento | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<DocumentoNoFormulario> | undefined> = {
  pessoaId: 'pessoaId',
  tipo: 'tipo',
  numero: 'numero',
  emissao: 'emissao',
  validade: 'validade',
  status: 'status',
  observacao: 'observacao',
};

/**
 * Converte o documento vindo da API no formato que o formulário guarda.
 *
 * @param documento - O documento a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais, com `null` trocado por string vazia.
 */
function valoresIniciais(documento: Documento | null): DocumentoNoFormulario {
  if (documento === null) return { ...DOCUMENTO_EM_BRANCO };

  return {
    pessoaId: documento.pessoa.id,
    tipo: documento.tipo,
    numero: documento.numero ?? '',
    emissao: documento.emissao ?? '',
    validade: documento.validade ?? '',
    status: documento.status,
    observacao: documento.observacao ?? '',
  };
}

/**
 * O formulário de criação e de edição de documento.
 *
 * @param documento - O documento a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioDocumento({
  documento,
  onSalvo,
  onCancelar,
}: FormularioDocumentoProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const pessoas = usePessoasFamilia();
  const salvar = useSalvarDocumento();

  const form = useForm<DocumentoNoFormulario, unknown, NovoDocumento>({
    resolver: zodResolver(novoDocumentoSchema),
    defaultValues: valoresIniciais(documento),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /**
   * Grava o documento e leva o erro da API para o campo que o causou.
   *
   * O que não tem caminho de campo — o 409, entre outros — vira a mensagem
   * acima dos botões; sem isso a gravação falharia sem que nada aparecesse.
   */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: documento?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="documento-pessoa">Pessoa</Label>
          <Controller
            control={form.control}
            name="pessoaId"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={enviando || pessoas.isPending}
              >
                <SelectTrigger id="documento-pessoa" className="w-full">
                  <SelectValue
                    placeholder={pessoas.isPending ? 'Carregando…' : 'Escolha de quem é'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(pessoas.data ?? []).map((pessoa) => (
                    <SelectItem key={pessoa.id} value={pessoa.id}>
                      {formatarPessoa(pessoa)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {pessoas.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhuma pessoa cadastrada ainda — cadastre em Cadastros-base antes.
            </p>
          )}
          {errors.pessoaId && (
            <p className="text-sm text-destructive" role="alert">
              {errors.pessoaId.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="documento-tipo">Documento</Label>
          <Input
            id="documento-tipo"
            autoComplete="off"
            placeholder="Ex: Passaporte, RG, CNH, Visto EUA"
            disabled={enviando}
            {...form.register('tipo')}
          />
          {errors.tipo && (
            <p className="text-sm text-destructive" role="alert">
              {errors.tipo.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="documento-numero">Número</Label>
          <Input
            id="documento-numero"
            autoComplete="off"
            placeholder="Ex: AB123456"
            disabled={enviando}
            {...form.register('numero')}
          />
          {errors.numero && (
            <p className="text-sm text-destructive" role="alert">
              {errors.numero.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="documento-emissao">Emissão</Label>
          <Controller
            control={form.control}
            name="emissao"
            render={({ field }) => (
              <CampoMascarado
                id="documento-emissao"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                placeholder="dd/mm/aaaa"
              />
            )}
          />
          {errors.emissao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.emissao.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="documento-validade">Validade</Label>
          <Controller
            control={form.control}
            name="validade"
            render={({ field }) => (
              <CampoMascarado
                id="documento-validade"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                placeholder="dd/mm/aaaa"
              />
            )}
          />
          {errors.validade && (
            <p className="text-sm text-destructive" role="alert">
              {errors.validade.message}
            </p>
          )}
        </div>

        <p className="col-span-full -mt-3 text-xs text-muted-foreground">
          Deixe a validade em branco no documento que não vence, como o RG.
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="documento-status">Status</Label>
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
                <SelectTrigger id="documento-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_NA_ORDEM_DO_FORMULARIO.map((status) => (
                    <SelectItem key={status} value={status}>
                      {ROTULOS_STATUS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.status && (
            <p className="text-sm text-destructive" role="alert">
              {errors.status.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="documento-observacao">Observações</Label>
          <Textarea
            id="documento-observacao"
            rows={3}
            placeholder="Notas adicionais"
            disabled={enviando}
            {...form.register('observacao')}
          />
          {errors.observacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.observacao.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar documento'}
        </Button>
      </DialogFooter>
    </form>
  );
}
